/**
 * Admin · taxonomy (owner-only, surface "platform") — the two CHEAP layers around the verticals
 * (Option B, docs/TAXONOMY_PROPOSAL.md):
 *
 *   Industries      ~20 flat labels that flavour generated questions and overlay guardrails.
 *                   They NEVER touch scoring or calibration — which is why this page can offer a
 *                   simple active toggle where Verticals needs editions and fingerprints.
 *   Role families   the per-vertical role vocabulary (family = mandatory pick, specialisations =
 *                   optional refinements, linked many-to-many). Content targeting only.
 *   Suggestions     free text recruiters typed when the catalogue had no word for their role.
 *                   Promoting a row is HOW the taxonomy learns — the alternative is us guessing.
 *
 * The Verticals page stays the only editor of anything a score depends on.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  INDUSTRIES,
  TAXONOMY_LIMITS,
  industryByKey,
  type Industry,
  type RoleFamily,
  type RoleFamilyLink,
  type RoleSpecialisation,
  type SuggestionLevel,
  type SuggestionStatus,
} from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { Alert, Info, Plus, Trash } from "../../components/icons.js";
import { Modal, Switch, apiErrorText } from "./adminUi.js";
import { useAdminVerticals } from "./Verticals.js";

// ── wire types ────────────────────────────────────────────────────────────────

/** DB industries carry an active flag on top of the shared shape (flavour/guardrails live in code). */
type AdminIndustry = Industry & { isActive?: boolean };

interface AdminTaxonomyResponse {
  industries: AdminIndustry[];
  families: RoleFamily[];
  specialisations: RoleSpecialisation[];
  links: RoleFamilyLink[];
}

interface SuggestionRow {
  id: string;
  rawText: string;
  level: SuggestionLevel;
  verticalKey: string | null;
  requisitionId: string | null;
  status: SuggestionStatus;
  promotedInto: string | null;
  createdAt: string;
}

const LEVEL_LABEL: Record<SuggestionLevel, string> = {
  industry: "Industry",
  role_family: "Role family",
  specialisation: "Specialisation",
};

/** A typed name → the snake_case key it becomes. Same shape the seed keys use. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 63);
}

const parseAliases = (text: string): string[] =>
  text
    .split(",")
    .map((a) => a.trim().toLowerCase())
    .filter((a) => a.length >= 2)
    .slice(0, TAXONOMY_LIMITS.maxAliasesPerRow)
    .map((a) => a.slice(0, TAXONOMY_LIMITS.maxAliasLen));

// ── page ──────────────────────────────────────────────────────────────────────

type Tab = "industries" | "roles" | "suggestions";

export function Taxonomy(): ReactNode {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("industries");
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-taxonomy"],
    queryFn: () => api.get<AdminTaxonomyResponse>("/admin/taxonomy"),
  });
  const suggestionsQuery = useQuery({
    queryKey: ["admin-taxonomy-suggestions"],
    queryFn: () => api.get<{ suggestions: SuggestionRow[] }>("/admin/taxonomy/suggestions"),
  });
  const suggestions = suggestionsQuery.data?.suggestions ?? [];
  const newCount = suggestions.filter((s) => s.status === "new").length;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["admin-taxonomy"] });
    void queryClient.invalidateQueries({ queryKey: ["admin-taxonomy-suggestions"] });
    // The recruiter-facing tree is a different cache key — without this a freshly promoted
    // specialisation stays invisible in New Request until a reload.
    void queryClient.invalidateQueries({ queryKey: ["taxonomy"] });
  };
  const onErr = (err: unknown): void =>
    setError(apiErrorText(err, "That change didn't go through. Please retry."));
  const clearErr = (): void => setError(null);

  const TABS: { id: Tab; label: string }[] = [
    { id: "industries", label: "Industries" },
    { id: "roles", label: "Role families & specialisms" },
    { id: "suggestions", label: `Suggestions${newCount > 0 ? ` (${newCount})` : ""}` },
  ];

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Taxonomy</h1>
          <div className="sub">
            Industries and role vocabulary around the verticals — question targeting, never scoring
          </div>
        </div>
      </div>

      <div className="banner info" style={{ marginBottom: 18 }}>
        <Info />
        <div>
          <b>Nothing on this page changes what a score means.</b> Industries flavour the questions
          and add guardrails; role families and specialisms narrow what the questions are about.
          Competencies, weights and anchors stay on the{" "}
          <b>Verticals</b> page — the only calibrated layer, with its edition history intact.
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`btn sm${tab === t.id ? " primary" : ""}`}
            onClick={() => {
              setTab(t.id);
              clearErr();
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && <div className="card pad mut sm">Loading the taxonomy…</div>}
      {isError && (
        <div className="banner crit">
          <Alert />
          <span>Couldn't load the taxonomy. Please retry in a moment.</span>
        </div>
      )}

      {error && (
        <div className="banner crit" style={{ marginBottom: 14 }}>
          <Alert />
          <span>{error}</span>
        </div>
      )}

      {!isLoading && !isError && data && tab === "industries" && (
        <IndustriesTab industries={data.industries} onDone={invalidate} onError={onErr} />
      )}
      {!isLoading && !isError && data && tab === "roles" && (
        <RolesTab data={data} onDone={invalidate} onError={onErr} />
      )}
      {tab === "suggestions" && (
        <SuggestionsTab
          suggestions={suggestions}
          loading={suggestionsQuery.isLoading}
          failed={suggestionsQuery.isError}
          catalogue={data ?? null}
          onDone={invalidate}
          onError={onErr}
        />
      )}
    </section>
  );
}

// ── Industries ────────────────────────────────────────────────────────────────

function IndustriesTab({
  industries,
  onDone,
  onError,
}: {
  industries: AdminIndustry[];
  onDone: () => void;
  onError: (err: unknown) => void;
}): ReactNode {
  const toggle = useMutation({
    mutationFn: (i: AdminIndustry) =>
      api.put(`/admin/taxonomy/industries/${encodeURIComponent(i.key)}`, {
        isActive: !(i.isActive !== false),
      }),
    onSuccess: onDone,
    onError,
  });
  // "Other" is where uncatalogued free text lands — the API refuses to switch it off, so the
  // switch shouldn't invite the attempt (same idiom as the default vertical).
  const isOther = (key: string): boolean => key === "other";

  return (
    <div className="card">
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Industry</th>
              <th>Recognised as</th>
              <th style={{ textAlign: "center" }}>Prompt overlay</th>
              <th style={{ textAlign: "center" }}>Active</th>
            </tr>
          </thead>
          <tbody>
            {industries.map((i) => {
              // Flavour and guardrails are prompt engineering: they live in code (reviewed like
              // code, tested equal to the seed) — shown here read-only so the owner can SEE what
              // an industry adds without this page becoming a prompt editor.
              const coded = industryByKey(i.key);
              const clauses = coded?.guardrails?.length ?? 0;
              return (
                <tr key={i.key}>
                  <td>
                    <div style={{ fontWeight: 650 }}>{i.name}</div>
                    <div className="mut sm" style={{ fontFamily: "var(--mono)", fontSize: 12 }}>
                      {i.key}
                    </div>
                    <div className="mut sm" style={{ marginTop: 3, maxWidth: 420 }}>
                      {i.description}
                    </div>
                  </td>
                  <td className="mut sm" style={{ maxWidth: 260 }}>
                    {i.aliases.length > 0 ? i.aliases.join(", ") : "—"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span
                      className={clauses > 0 ? "pill t" : "pill n"}
                      title={
                        [
                          coded?.flavour ? `Flavour: ${coded.flavour}` : null,
                          ...(coded?.guardrails ?? []).map((g) => `Guardrail: ${g}`),
                        ]
                          .filter(Boolean)
                          .join("\n\n") ||
                        "No flavour or guardrail overlay — the vertical's own rules apply unchanged."
                      }
                    >
                      {coded?.flavour ? "flavour" : "—"}
                      {clauses > 0 ? ` · ${clauses} guardrail${clauses === 1 ? "" : "s"}` : ""}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <div
                      style={{ display: "inline-flex" }}
                      title={isOther(i.key) ? "Where anything uncatalogued lands — it always stays on" : undefined}
                    >
                      <Switch
                        on={i.isActive !== false}
                        disabled={toggle.isPending || isOther(i.key)}
                        onToggle={() => toggle.mutate(i)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="card-b">
        <p className="mut sm" style={{ margin: 0 }}>
          Flavour sentences and guardrail clauses are code-reviewed (they are sent to the model
          verbatim), so they aren't editable here. New industries arrive through the suggestions
          queue — "Other" plus a typed name is the funnel.
        </p>
      </div>
    </div>
  );
}

// ── Role families & specialisations ───────────────────────────────────────────

interface FamilyDraft {
  isNew: boolean;
  verticalKey: string;
  key: string;
  name: string;
  description: string;
  aliasesText: string;
  // PUT is a full replace with defaults (true/0) — these ride along so an edit can never silently
  // reactivate a switched-off family or send it to the top of the picker.
  isActive: boolean;
  sortOrder: number;
}

function RolesTab({
  data,
  onDone,
  onError,
}: {
  data: AdminTaxonomyResponse;
  onDone: () => void;
  onError: (err: unknown) => void;
}): ReactNode {
  const { verticals } = useAdminVerticals();
  const [verticalKey, setVerticalKey] = useState<string>("");
  const [draft, setDraft] = useState<FamilyDraft | null>(null);
  const [linkFor, setLinkFor] = useState<string | null>(null); // family key an owner is linking into
  const [newSpecName, setNewSpecName] = useState("");

  const vk =
    verticalKey ||
    [...verticals].sort((a, b) => a.sortOrder - b.sortOrder)[0]?.key ||
    data.families[0]?.verticalKey ||
    "";

  const families = data.families
    .filter((f) => f.verticalKey === vk)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  const verticalSpecs = data.specialisations.filter((s) => s.verticalKey === vk);
  const linksOf = (familyKey: string): Set<string> =>
    new Set(
      data.links
        .filter((l) => l.verticalKey === vk && l.familyKey === familyKey)
        .map((l) => l.specKey),
    );

  const saveFamily = useMutation({
    mutationFn: (d: FamilyDraft) => {
      // PUT replaces the whole editable surface (defaults true/0), so EVERYTHING is echoed —
      // same lesson the vertical toggle learnt about protectedTopics.
      const body = {
        name: d.name.trim(),
        description: d.description.trim(),
        aliases: parseAliases(d.aliasesText),
        isActive: d.isActive,
        sortOrder: d.sortOrder,
      };
      return d.isNew
        ? api.post("/admin/taxonomy/families", { ...body, verticalKey: d.verticalKey, key: d.key.trim() })
        : api.put(
            `/admin/taxonomy/families/${encodeURIComponent(d.verticalKey)}/${encodeURIComponent(d.key)}`,
            body,
          );
    },
    onSuccess: () => {
      setDraft(null);
      onDone();
    },
    onError,
  });

  /** On/off switch — echoes the full row back (PUT is a whole-surface replace, see saveFamily). */
  const toggleFamily = useMutation({
    mutationFn: (f: RoleFamily) =>
      api.put(`/admin/taxonomy/families/${encodeURIComponent(f.verticalKey)}/${encodeURIComponent(f.key)}`, {
        name: f.name,
        description: f.description,
        aliases: f.aliases,
        isActive: !f.isActive,
        sortOrder: f.sortOrder,
      }),
    onSuccess: onDone,
    onError,
  });

  const link = useMutation({
    mutationFn: (b: { familyKey: string; specKey: string }) =>
      api.post("/admin/taxonomy/links", { verticalKey: vk, ...b }),
    onSuccess: onDone,
    onError,
  });
  const unlink = useMutation({
    mutationFn: (b: { familyKey: string; specKey: string }) =>
      api.del(
        `/admin/taxonomy/links/${encodeURIComponent(vk)}/${encodeURIComponent(b.familyKey)}/${encodeURIComponent(b.specKey)}`,
      ),
    onSuccess: onDone,
    onError,
  });

  /** Create a brand-new specialisation AND link it to the family in one intent — the two-step
   *  version guaranteed orphan rows whenever an owner stopped halfway. */
  const createSpec = useMutation({
    mutationFn: async (b: { familyKey: string; name: string }) => {
      const key = slugify(b.name);
      await api.post("/admin/taxonomy/specialisations", {
        verticalKey: vk,
        key,
        name: b.name.trim(),
        aliases: [],
      });
      await api.post("/admin/taxonomy/links", { verticalKey: vk, familyKey: b.familyKey, specKey: key });
    },
    onSuccess: () => {
      setNewSpecName("");
      onDone();
    },
    onError,
  });

  return (
    <>
      <div className="card pad" style={{ marginBottom: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <label htmlFor="tx-vertical" style={{ fontWeight: 650, fontSize: 13 }}>
          Vertical
        </label>
        <select
          id="tx-vertical"
          className="input"
          style={{ maxWidth: 320 }}
          value={vk}
          onChange={(e) => setVerticalKey(e.target.value)}
        >
          {[...verticals]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((v) => (
              <option key={v.key} value={v.key}>
                {v.name}
              </option>
            ))}
        </select>
        <span className="mut sm">
          {families.length} famil{families.length === 1 ? "y" : "ies"} · {verticalSpecs.length}{" "}
          specialisms in this vertical
        </span>
        <span style={{ flex: 1 }} />
        <button
          className="btn sm primary"
          onClick={() =>
            setDraft({
              isNew: true,
              verticalKey: vk,
              key: "",
              name: "",
              description: "",
              aliasesText: "",
              isActive: true,
              sortOrder: families.reduce((a, f) => Math.max(a, f.sortOrder), 0) + 10,
            })
          }
        >
          <Plus /> New family
        </button>
      </div>

      {families.length === 0 && (
        <div className="card pad mut sm">No role families under this vertical yet.</div>
      )}

      {families.map((f) => {
        const linked = linksOf(f.key);
        const specs = verticalSpecs
          .filter((s) => linked.has(s.key))
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
        const linkable = verticalSpecs
          .filter((s) => !linked.has(s.key) && s.isActive)
          .sort((a, b) => a.name.localeCompare(b.name));
        return (
          <div key={f.key} className="card" style={{ marginBottom: 12 }}>
            <div className="card-h">
              <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {f.name}
                {f.isSystem && (
                  <span className="pill n" style={{ fontSize: 12 }} title="Seeded — re-wordable, never deletable (historical requisitions name it)">
                    System
                  </span>
                )}
                {!f.isActive && <span className="pill n" style={{ fontSize: 12 }}>Off</span>}
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  className="btn sm"
                  onClick={() =>
                    setDraft({
                      isNew: false,
                      verticalKey: f.verticalKey,
                      key: f.key,
                      name: f.name,
                      description: f.description,
                      aliasesText: f.aliases.join(", "),
                      isActive: f.isActive,
                      sortOrder: f.sortOrder,
                    })
                  }
                >
                  Edit
                </button>
                <Switch on={f.isActive} disabled={toggleFamily.isPending} onToggle={() => toggleFamily.mutate(f)} />
              </div>
            </div>
            <div className="card-b">
              <div className="mut sm" style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{f.key}</div>
              {f.description && <p className="mut sm" style={{ margin: "4px 0 0" }}>{f.description}</p>}
              {f.aliases.length > 0 && (
                <p className="mut sm" style={{ margin: "4px 0 0" }}>
                  Recognised as: {f.aliases.join(", ")}
                </p>
              )}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
                {specs.map((s) => (
                  <span key={s.key} className="pill t" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    {s.name}
                    <button
                      type="button"
                      aria-label={`Unlink ${s.name}`}
                      data-tip="Unlink from this family"
                      style={{ border: "none", background: "transparent", cursor: "pointer", color: "inherit", padding: 0, lineHeight: 1 }}
                      disabled={unlink.isPending}
                      onClick={() => unlink.mutate({ familyKey: f.key, specKey: s.key })}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {specs.length === 0 && <span className="mut sm">No specialisms linked.</span>}
                <button className="btn sm ghost" onClick={() => setLinkFor(linkFor === f.key ? null : f.key)}>
                  <Plus /> Link
                </button>
              </div>

              {linkFor === f.key && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
                  {linkable.length > 0 && (
                    <select
                      className="input"
                      style={{ maxWidth: 280 }}
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) link.mutate({ familyKey: f.key, specKey: e.target.value });
                        e.target.value = "";
                      }}
                    >
                      <option value="">Link an existing specialism…</option>
                      {linkable.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <input
                    className="input"
                    style={{ maxWidth: 240 }}
                    placeholder="…or create a new one"
                    maxLength={80}
                    value={newSpecName}
                    onChange={(e) => setNewSpecName(e.target.value)}
                  />
                  <button
                    className="btn sm"
                    disabled={newSpecName.trim().length < 2 || createSpec.isPending}
                    onClick={() => createSpec.mutate({ familyKey: f.key, name: newSpecName })}
                  >
                    {createSpec.isPending ? "Adding…" : "Add & link"}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {draft && (
        <Modal
          title={draft.isNew ? "New role family" : `Edit ${draft.name || draft.key}`}
          onClose={() => setDraft(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setDraft(null)} disabled={saveFamily.isPending}>
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={
                  saveFamily.isPending ||
                  draft.name.trim().length < 2 ||
                  (draft.isNew && slugify(draft.key || draft.name).length < 2)
                }
                onClick={() =>
                  saveFamily.mutate(
                    draft.isNew ? { ...draft, key: draft.key.trim() || slugify(draft.name) } : draft,
                  )
                }
              >
                {saveFamily.isPending ? "Saving…" : draft.isNew ? "Create family" : "Save changes"}
              </button>
            </div>
          }
        >
          <div className="field">
            <label>Name</label>
            <input
              className="input"
              value={draft.name}
              maxLength={80}
              placeholder="e.g. Backend engineer"
              onChange={(e) =>
                setDraft({
                  ...draft,
                  name: e.target.value,
                  // The key follows the name while it's still being typed — never after creation.
                  ...(draft.isNew ? { key: slugify(e.target.value) } : {}),
                })
              }
            />
          </div>
          <div className="field">
            <label>Key</label>
            <input
              className="input"
              value={draft.key}
              maxLength={63}
              disabled={!draft.isNew}
              style={{ fontFamily: "var(--mono)" }}
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
            />
            <div className="hint">
              {draft.isNew
                ? "Lower-case snake_case. Permanent — requisitions record it as a historical statement."
                : "Fixed once created: requisitions already carry this key."}
            </div>
          </div>
          <div className="field">
            <label>Description</label>
            <textarea
              className="input"
              rows={2}
              maxLength={400}
              value={draft.description}
              placeholder="What this family covers — shown under the recruiter's picker."
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Aliases</label>
            <textarea
              className="input"
              rows={2}
              value={draft.aliasesText}
              placeholder="comma, separated, phrases a JD actually uses"
              onChange={(e) => setDraft({ ...draft, aliasesText: e.target.value })}
            />
            <div className="hint">
              Lower-case phrases the JD matcher looks for — "sde", "server side developer". Up to{" "}
              {TAXONOMY_LIMITS.maxAliasesPerRow}.
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Suggestions queue ─────────────────────────────────────────────────────────

/**
 * Mirrors the API's promoteSchema: a suggestion becomes either an ALIAS on an existing row (the
 * common case — same concept, different words) or a NEW custom row. The level is the suggestion's
 * own, never re-chosen here; industries are alias-only because a new industry means new reviewed
 * prompt text (a code change), not a row.
 */
interface PromoteDraft {
  suggestion: SuggestionRow;
  as: "alias" | "new_row";
  verticalKey: string;
  targetKey: string; // alias mode: the row that gains the alias
  familyKey: string; // new_row specialisation: also link into this family
  name: string;
  key: string;
}

function SuggestionsTab({
  suggestions,
  loading,
  failed,
  catalogue,
  onDone,
  onError,
}: {
  suggestions: SuggestionRow[];
  loading: boolean;
  failed: boolean;
  catalogue: AdminTaxonomyResponse | null;
  onDone: () => void;
  onError: (err: unknown) => void;
}): ReactNode {
  const { verticals } = useAdminVerticals();
  const [promote, setPromote] = useState<PromoteDraft | null>(null);
  const fresh = suggestions.filter((s) => s.status === "new");
  const handled = suggestions.filter((s) => s.status !== "new");

  const promoteMut = useMutation({
    mutationFn: (d: PromoteDraft) =>
      api.post(`/admin/taxonomy/suggestions/${d.suggestion.id}/promote`, {
        as: d.as,
        ...(d.suggestion.level !== "industry" ? { verticalKey: d.verticalKey } : {}),
        ...(d.as === "alias"
          ? { targetKey: d.targetKey }
          : {
              key: d.key.trim() || slugify(d.name),
              name: d.name.trim(),
              ...(d.suggestion.level === "specialisation" && d.familyKey ? { familyKey: d.familyKey } : {}),
            }),
      }),
    onSuccess: () => {
      setPromote(null);
      onDone();
    },
    onError,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => api.post(`/admin/taxonomy/suggestions/${id}/dismiss`, {}),
    onSuccess: onDone,
    onError,
  });

  if (loading) return <div className="card pad mut sm">Loading suggestions…</div>;
  if (failed)
    return (
      <div className="banner crit">
        <Alert />
        <span>Couldn't load the suggestions queue. Please retry in a moment.</span>
      </div>
    );

  if (suggestions.length === 0)
    return (
      <div className="card pad" style={{ textAlign: "center" }}>
        <p style={{ fontWeight: 650, margin: "0 0 6px" }}>Nothing waiting — and that's the system working.</p>
        <p className="mut sm" style={{ margin: "0 auto", maxWidth: 520 }}>
          When a recruiter types a role or industry the catalogue doesn't have a word for, it lands
          here (and is still used immediately for their own request). Promoting a row into the
          taxonomy is how the vocabulary learns from real demand instead of our guesses.
        </p>
      </div>
    );

  const row = (s: SuggestionRow): ReactNode => (
    <tr key={s.id}>
      <td style={{ fontWeight: 650 }}>“{s.rawText}”</td>
      <td>
        <span className="pill n">{LEVEL_LABEL[s.level]}</span>
      </td>
      <td className="mut sm">{s.verticalKey ? s.verticalKey.replace(/_/g, " ") : "—"}</td>
      <td className="mut sm">{new Date(s.createdAt).toLocaleDateString()}</td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {s.status === "new" ? (
          <>
            <button
              className="btn sm"
              onClick={() =>
                setPromote({
                  suggestion: s,
                  // Alias-on-existing is the common case (and the only one industries allow).
                  as: "alias",
                  verticalKey: s.verticalKey ?? verticals[0]?.key ?? "",
                  targetKey: "",
                  familyKey: "",
                  name: s.rawText,
                  key: slugify(s.rawText),
                })
              }
            >
              Promote…
            </button>
            <button
              className="btn sm ghost"
              style={{ marginLeft: 6 }}
              disabled={dismiss.isPending}
              aria-label="Dismiss — kept on record, never re-suggested"
              data-tip="Dismiss"
              data-tip-pos="left"
              onClick={() => dismiss.mutate(s.id)}
            >
              <Trash />
            </button>
          </>
        ) : (
          <span className={s.status === "promoted" ? "pill v" : "pill n"}>
            {s.status === "promoted" ? `promoted${s.promotedInto ? ` → ${s.promotedInto}` : ""}` : "dismissed"}
          </span>
        )}
      </td>
    </tr>
  );

  return (
    <>
      <div className="card">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>What was typed</th>
                <th>Level</th>
                <th>Vertical</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {fresh.map(row)}
              {handled.map(row)}
            </tbody>
          </table>
        </div>
      </div>

      {promote && (() => {
        const level = promote.suggestion.level;
        const isIndustry = level === "industry";
        // Alias targets: the level's own rows, scoped to the chosen vertical for role levels.
        const aliasTargets: { key: string; name: string }[] = isIndustry
          ? (catalogue?.industries ?? INDUSTRIES.map((i) => ({ key: i.key, name: i.name })))
          : level === "role_family"
            ? (catalogue?.families ?? []).filter((f) => f.verticalKey === promote.verticalKey)
            : (catalogue?.specialisations ?? []).filter((s) => s.verticalKey === promote.verticalKey);
        const vFamilies = (catalogue?.families ?? [])
          .filter((f) => f.verticalKey === promote.verticalKey)
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
        const canSubmit =
          !promoteMut.isPending &&
          (isIndustry || promote.verticalKey !== "") &&
          (promote.as === "alias"
            ? promote.targetKey !== ""
            : promote.name.trim().length >= 2 && slugify(promote.key || promote.name).length >= 2);
        return (
          <Modal
            title={`Promote “${promote.suggestion.rawText}”`}
            onClose={() => setPromote(null)}
            footer={
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn" onClick={() => setPromote(null)} disabled={promoteMut.isPending}>
                  Cancel
                </button>
                <button className="btn primary" disabled={!canSubmit} onClick={() => promoteMut.mutate(promote)}>
                  {promoteMut.isPending ? "Promoting…" : "Promote"}
                </button>
              </div>
            }
          >
            <p className="mut sm" style={{ marginTop: 0 }}>
              A recruiter typed this because the catalogue had no word for it. Most of the time the
              concept already exists under a different name — make the phrase an <b>alias</b> so it
              matches next time. Only mint a <b>new {LEVEL_LABEL[level].toLowerCase()}</b> when the
              catalogue genuinely lacks it.
            </p>
            <div className="field">
              <label>How</label>
              <select
                className="input"
                value={promote.as}
                onChange={(e) => setPromote({ ...promote, as: e.target.value as "alias" | "new_row" })}
              >
                <option value="alias">Alias on an existing {LEVEL_LABEL[level].toLowerCase()}</option>
                {/* Industries are alias-only: a new industry means new code-reviewed flavour +
                    guardrail prompt text, so the API refuses new_row at this level. */}
                {!isIndustry && <option value="new_row">New {LEVEL_LABEL[level].toLowerCase()}</option>}
              </select>
            </div>
            {!isIndustry && (
              <div className="field">
                <label>Vertical</label>
                <select
                  className="input"
                  value={promote.verticalKey}
                  onChange={(e) =>
                    setPromote({ ...promote, verticalKey: e.target.value, targetKey: "", familyKey: "" })
                  }
                >
                  <option value="">Select a vertical…</option>
                  {[...verticals]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((v) => (
                      <option key={v.key} value={v.key}>
                        {v.name}
                      </option>
                    ))}
                </select>
              </div>
            )}
            {promote.as === "alias" && (
              <div className="field">
                <label>Becomes an alias of</label>
                <select
                  className="input"
                  value={promote.targetKey}
                  onChange={(e) => setPromote({ ...promote, targetKey: e.target.value })}
                >
                  <option value="">Select…</option>
                  {aliasTargets.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {promote.as === "new_row" && !isIndustry && (
              <>
                {level === "specialisation" && (
                  <div className="field">
                    <label>Link into family</label>
                    <select
                      className="input"
                      value={promote.familyKey}
                      onChange={(e) => setPromote({ ...promote, familyKey: e.target.value })}
                    >
                      <option value="">Select a role family…</option>
                      {vFamilies.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                    <div className="hint">
                      A specialism no family links to never appears in the wizard — link it on the
                      way in.
                    </div>
                  </div>
                )}
                <div className="grid g2">
                  <div className="field">
                    <label>Name</label>
                    <input
                      className="input"
                      value={promote.name}
                      maxLength={120}
                      onChange={(e) =>
                        setPromote({ ...promote, name: e.target.value, key: slugify(e.target.value) })
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Key</label>
                    <input
                      className="input"
                      value={promote.key}
                      maxLength={63}
                      style={{ fontFamily: "var(--mono)" }}
                      onChange={(e) => setPromote({ ...promote, key: e.target.value })}
                    />
                  </div>
                </div>
              </>
            )}
          </Modal>
        );
      })()}
    </>
  );
}
